import { Users, Target, TrendingUp, BarChart3, Heart, Megaphone } from "lucide-react";
import SubServicePageLayout from "@/components/SubServicePageLayout";

const services = [
  {
    name: "Strategy & Planning",
    icon: Target,
    components: [
      "Campaign objective definition",
      "Audience and persona alignment",
      "Platform and format selection",
    ],
    outcome: "Clear strategy that connects influencer activity to business goals.",
  },
  {
    name: "Influencer Discovery",
    icon: Users,
    components: [
      "Audience authenticity analysis",
      "Engagement quality assessment",
      "Brand safety and fit evaluation",
    ],
    outcome: "Partners who genuinely reach your target audience.",
  },
  {
    name: "Campaign Management",
    icon: Megaphone,
    components: [
      "Brief development and negotiation",
      "Content review and approval",
      "Timeline and deliverable management",
    ],
    outcome: "Seamless execution without brand compromises.",
  },
  {
    name: "Creative Collaboration",
    icon: Heart,
    components: [
      "Concept development with creators",
      "Brand guideline integration",
      "Content optimization for performance",
    ],
    outcome: "Authentic content that drives action.",
  },
  {
    name: "Performance Measurement",
    icon: BarChart3,
    components: [
      "Multi-platform tracking and attribution",
      "ROI and ROAS calculation",
      "Benchmarking and optimization",
    ],
    outcome: "Clear visibility into what's working and why.",
  },
  {
    name: "Amplification & Scale",
    icon: TrendingUp,
    components: [
      "Paid amplification of top content",
      "Whitelisting and brand partnership ads",
      "UGC repurposing across channels",
    ],
    outcome: "Extend the value of creator content beyond organic reach.",
  },
];

const processSteps = [
  {
    number: "01",
    title: "Strategy Development",
    description: "Define objectives, target audience, and success metrics aligned with business goals.",
  },
  {
    number: "02",
    title: "Influencer Research",
    description: "Identify and vet potential partners based on audience quality, engagement, and brand fit.",
  },
  {
    number: "03",
    title: "Outreach & Negotiation",
    description: "Secure partnerships with clear deliverables, usage rights, and performance expectations.",
  },
  {
    number: "04",
    title: "Campaign Execution",
    description: "Manage content creation, review, and publishing across all selected platforms.",
  },
  {
    number: "05",
    title: "Analysis & Optimization",
    description: "Measure performance, extract learnings, and optimize ongoing and future campaigns.",
  },
];

const technologies = [
  { category: "Discovery", items: ["Grin", "CreatorIQ", "Upfluence", "Aspire"] },
  { category: "Measurement", items: ["Traackr", "Impact", "Klear", "Sprout Social"] },
  { category: "Platforms", items: ["Instagram", "TikTok", "YouTube", "LinkedIn"] },
  { category: "Amplification", items: ["Meta Ads", "TikTok Spark Ads", "YouTube BrandConnect"] },
];

const faqs = [
  {
    question: "How do you measure influencer marketing ROI?",
    answer: "We track both direct metrics (clicks, conversions, revenue) and brand metrics (reach, engagement, sentiment). We use unique tracking links, promo codes, and post-purchase surveys to attribute accurately.",
  },
  {
    question: "Do you work with macro or micro influencers?",
    answer: "Both, depending on your objectives. Micro-influencers often deliver better engagement rates and authenticity, while macro-influencers provide reach. Most campaigns include a mix.",
  },
  {
    question: "How do you ensure brand safety?",
    answer: "We thoroughly vet every influencer's content history, audience demographics, and brand associations. We also implement clear contracts and content approval processes.",
  },
  {
    question: "What's your approach to influencer relationships?",
    answer: "We believe in long-term partnerships over one-off posts. Authentic, repeated endorsements build more trust than single mentions.",
  },
  {
    question: "Can you integrate influencer content with our paid media?",
    answer: "Absolutely. Whitelisting and partnership ads allow us to amplify top-performing creator content through paid channels, often outperforming traditional ads.",
  },
];

const qualification = {
  forYou: [
    "You have a consumer-facing brand with a clear target audience",
    "You're willing to invest in building authentic creator relationships",
    "You understand that influencer marketing is a channel, not a shortcut",
    "You have budget for both creator fees and paid amplification",
  ],
  notForYou: [
    "You're looking for viral success from a single post",
    "You want to micro-manage every piece of content",
    "You're not willing to give creators creative freedom",
    "You expect influencer marketing to replace your entire marketing strategy",
  ],
};

const InfluencerMarketing = () => {
  return (
    <SubServicePageLayout
      seoTitle="Influencer Marketing Services"
      seoDescription="Performance-driven influencer marketing campaigns. From creator discovery to measurement. Reach, engagement, and revenue through authentic partnerships."
      canonicalUrl="https://xperfomax.com/media-activations/influencer-marketing"
      category="media-activations"
      categoryLabel="Media Activations / Influencer Marketing"
      headline="Influencer marketing that performs."
      subheadline="We connect brands with creators who authentically reach your audience. And we measure results beyond vanity metrics."
      problemTitle="Why most influencer campaigns disappoint."
      problems={[
        "Chasing follower counts instead of audience quality.",
        "No clear connection between content and business outcomes.",
        "One-off posts that don't build lasting brand associations.",
        "Poor measurement makes it impossible to optimize.",
      ]}
      systemTitle="A performance system for influencer marketing."
      systemDescription="Strategy, discovery, execution, and measurement working together."
      services={services}
      processTitle="From strategy to scale."
      processSteps={processSteps}
      technologies={technologies}
      techTitle="Platforms & Tools"
      techDescription="We use industry-leading tools for discovery, management, and measurement."
      qualification={qualification}
      faqs={faqs}
      finalCtaTitle="Ready for influencer marketing that works?"
      finalCtaDescription="Let's discuss how creator partnerships can drive real business results."
      finalCtaButtonText="Start a serious conversation"
    />
  );
};

export default InfluencerMarketing;
